import { addDoc, collection, doc, getDoc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../../../../backend/firebaseConfig";
import { assertChargeDatesAreOpen } from "../../../Accounts/MonthlyPayments/utils/closedChargeMonths";

const LICENCE_PATH = ["Marketing", "EmailLicences", "Items"];
const licenceCollection = () => collection(db, ...LICENCE_PATH);
const licenceAuditCollection = (id) => collection(db, ...LICENCE_PATH, id, "AuditLogs");
const trim = (value) => String(value ?? "").trim();

const payload = (data) => ({
  orderDate: trim(data.orderDate),
  adviserName: trim(data.adviserName),
  adviserEmail: trim(data.adviserEmail).toLowerCase(),
  licenceType: trim(data.licenceType),
  numberOfLicences: trim(data.numberOfLicences) || "1",
  emailAddress: trim(data.emailAddress)
    .split(",")
    .map((address) => address.trim().toLowerCase())
    .filter(Boolean)
    .join(", "),
  domainLinked: trim(data.domainLinked)
    .split(",")
    .map((domain) => domain.trim().toLowerCase())
    .filter(Boolean)
    .join(", "),
  monthlyAmount: trim(data.monthlyAmount),
  chargeFromDate: trim(data.chargeFromDate) || trim(data.orderDate),
  notes: trim(data.notes),
});

const uploadInvoice = async (id, file) => {
  if (!(file instanceof File)) return file || null;
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const fileRef = ref(storage, `Marketing/EmailLicences/${id}/invoices/${Date.now()}-${safeName}`);
  await uploadBytes(fileRef, file, { contentType: file.type });
  return { name: file.name, type: file.type || "application/octet-stream", size: file.size, storagePath: fileRef.fullPath, url: await getDownloadURL(fileRef) };
};

const actorData = (actor = {}) => ({ uid: actor.uid || "", email: actor.email || "", displayName: actor.displayName || "" });
const getChanges = (before, after) => Object.keys(after).reduce((changes, field) => {
  if (JSON.stringify(before?.[field] ?? null) !== JSON.stringify(after[field] ?? null)) changes[field] = { before: before?.[field] ?? null, after: after[field] ?? null };
  return changes;
}, {});
const addAuditLog = (id, action, actor, changes) => addDoc(licenceAuditCollection(id), {
  action, actor: actorData(actor), changes, changedFields: Object.keys(changes), createdAt: serverTimestamp(), createdAtMs: Date.now(),
});

export const createEmailLicence = async ({ formData, invoice, actor }) => {
  const formPayload = payload(formData);
  await assertChargeDatesAreOpen([formPayload.chargeFromDate]);
  const licenceRef = await addDoc(licenceCollection(), {
    ...formPayload, invoice: null, createdBy: actorData(actor),
    createdAt: serverTimestamp(), createdAtMs: Date.now(),
  });
  const uploaded = await uploadInvoice(licenceRef.id, invoice);
  if (uploaded) await updateDoc(licenceRef, { invoice: uploaded });
  await addAuditLog(licenceRef.id, "created", actor, getChanges({}, { ...formPayload, invoice: uploaded }));
  return licenceRef.id;
};

export const updateEmailLicence = async ({ licenceId, formData, invoice, actor }) => {
  const licenceRef = doc(db, ...LICENCE_PATH, licenceId);
  const currentSnapshot = await getDoc(licenceRef);
  if (!currentSnapshot.exists()) throw new Error("Email licence not found.");
  const formPayload = payload(formData);
  await assertChargeDatesAreOpen([
    currentSnapshot.data().chargeFromDate || currentSnapshot.data().orderDate,
    formPayload.chargeFromDate,
  ]);
  const uploaded = await uploadInvoice(licenceId, invoice);
  const next = { ...formPayload, invoice: uploaded };
  const changes = getChanges(currentSnapshot.data(), next);
  if (!Object.keys(changes).length) return;
  await updateDoc(licenceRef, {
    ...next, updatedBy: actorData(actor),
    updatedAt: serverTimestamp(), updatedAtMs: Date.now(),
  });
  await addAuditLog(licenceId, "updated", actor, changes);
};

export const subscribeEmailLicences = (callback, onError) => onSnapshot(
  query(licenceCollection(), orderBy("createdAtMs", "desc")),
  (snapshot) => callback(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))), onError,
);

export const subscribeEmailLicence = (licenceId, callback, onError) => onSnapshot(
  doc(db, ...LICENCE_PATH, licenceId),
  (snapshot) => callback(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null), onError,
);

export const subscribeEmailLicenceAuditLogs = (licenceId, callback, onError) => onSnapshot(
  query(licenceAuditCollection(licenceId), orderBy("createdAtMs", "desc")),
  (snapshot) => callback(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))), onError,
);
